import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

interface CacheEntry<T> {
  obs$: Observable<T>;
  timestamp: number;
  ttl: number;
}

@Injectable({ providedIn: 'root' })
export class RequestCacheService {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 7000; // 7 seconds

  constructor(private http: HttpClient) { }

  private buildKey(method: string, url: string, body?: any): string {
    return `${method}:${url}:${body ? JSON.stringify(body) : ''}`;
  }

  request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    options: {
      body?: any;
      headers?: HttpHeaders;
      ttl?: number; // custom TTL in ms
    } = {}
  ): Observable<T> {
    const key = this.buildKey(method, url, options.body);
    const cached = this.cache.get(key);
    const ttl = options.ttl ?? this.DEFAULT_TTL;

    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.obs$;
    }

    let req$: Observable<T>;
    switch (method) {
      case 'GET':
        req$ = this.http.get<T>(url, { headers: options.headers });
        break;
      case 'POST':
        req$ = this.http.post<T>(url, options.body, { headers: options.headers });
        break;
      case 'PUT':
        req$ = this.http.put<T>(url, options.body, { headers: options.headers });
        break;
      case 'DELETE':
        req$ = this.http.delete<T>(url, { headers: options.headers });
        break;
      default:
        throw new Error(`Unsupported method: ${method}`);
    }

    const sharedReq$ = req$.pipe(
      shareReplay(1),
      catchError(err => {
        this.cache.delete(key);
        return throwError(() => err);
      })
    );

    this.cache.set(key, { obs$: sharedReq$, timestamp: Date.now(), ttl });
    return sharedReq$;
  }

  get<T>(url: string, headers?: HttpHeaders, ttl?: number) {
    return this.request<T>('GET', url, { headers, ttl });
  }

  post<T>(url: string, body: any, headers?: HttpHeaders, ttl?: number) {
    return this.request<T>('POST', url, { body, headers, ttl });
  }

  put<T>(url: string, body: any, headers?: HttpHeaders, ttl?: number) {
    return this.request<T>('PUT', url, { body, headers, ttl });
  }

  delete<T>(url: string, headers?: HttpHeaders, ttl?: number) {
    return this.request<T>('DELETE', url, { headers, ttl });
  }

  invalidate(method: 'GET' | 'POST' | 'PUT' | 'DELETE', url: string, body?: any) {
    const key = this.buildKey(method, url, body);
    this.cache.delete(key);
  }

  invalidateAllForUrl(url: string) {
    for (const key of this.cache.keys()) {
      if (key.includes(`:${url}:`)) {
        this.cache.delete(key);
      }
    }
  }

  invalidateAllForUrlParts(urlParts: string[]) {
    for (const key of this.cache.keys()) {
      let foundMatch = true;
      for (const urlPart of urlParts) {
        if (!key.includes(urlPart)) {
          foundMatch = false;
          break;
        }
      }
      if (foundMatch) {
        this.cache.delete(key);
      }
    }
  }

  clearAll() {
    this.cache.clear();
  }
}
